import CloseIcon from "@mui/icons-material/Close";
import MinimizeIcon from "@mui/icons-material/Minimize";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VerticalSplitIcon from "@mui/icons-material/VerticalSplit";
import { Box, IconButton, Tooltip } from "@mui/material";

import { getHeaderBackgroundColor, HEADER_HEIGHT } from "../constants";
import type { SplitLayout } from "../hooks/useTabGroup";
import type { ImpersonationSession } from "../types";

interface TabGroupHeaderProps {
  dockedSessions: ImpersonationSession[];
  isWarning: boolean;
  width: number;
  splitLayout: SplitLayout | null;
  onDragStart: (event: React.PointerEvent) => void;
  onDragMove: (event: React.PointerEvent) => void;
  onDragEnd: (event: React.PointerEvent) => void;
  onExitSplitView: () => void;
  onPopOutSession: (id: string) => void;
  onMinimizeTabGroup: () => void;
  onCloseAll: () => void;
}

export function TabGroupHeader({
  dockedSessions,
  isWarning,
  width,
  splitLayout,
  onDragStart,
  onDragMove,
  onDragEnd,
  onExitSplitView,
  onPopOutSession,
  onMinimizeTabGroup,
  onCloseAll,
}: TabGroupHeaderProps) {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        height: HEADER_HEIGHT,
        paddingX: 1,
        backgroundColor: getHeaderBackgroundColor(false, undefined, isWarning),
        color: theme.palette.common.white,
        cursor: "grab",
        width,
        boxSizing: "border-box",
      })}
      onPointerDown={onDragStart}
      onPointerMove={onDragMove}
      onPointerUp={onDragEnd}
    >
      <Box
        component="span"
        sx={{
          flex: 1,
          fontWeight: 600,
          fontSize: "0.75rem",
          pointerEvents: "none",
        }}
      >
        Impersonation Sessions ({dockedSessions.length})
      </Box>
      {splitLayout && (
        <Tooltip title="Exit split view">
          <IconButton
            size="small"
            aria-label="Exit split view"
            sx={(theme) => ({ color: theme.palette.common.white, padding: 0.25 })}
            onClick={onExitSplitView}
          >
            <VerticalSplitIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <IconButton
        size="small"
        aria-label="Pop out all sessions"
        sx={(theme) => ({ color: theme.palette.common.white, padding: 0.25 })}
        onClick={() => {
          for (const session of dockedSessions) {
            onPopOutSession(session.id);
          }
        }}
      >
        <OpenInNewIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Minimize tab group"
        sx={(theme) => ({ color: theme.palette.common.white, padding: 0.25 })}
        onClick={onMinimizeTabGroup}
      >
        <MinimizeIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Close all docked sessions"
        sx={(theme) => ({ color: theme.palette.common.white, padding: 0.25 })}
        onClick={onCloseAll}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
